import './app.less';
import { useThrottleFn } from 'ahooks';
import { floor } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { IExportPlugin, PluginType, type ISize } from '@/version-types';
import { CoreAPI, useIdentity, useVersion } from './core';
import { DragAndDropLayer, Listener } from './operate';
import {
    StageAPI,
    StageNode,
    useActiveStageIndex,
    useIsAutoScale,
    useIsLongImageMode,
    useIsStageVisible,
    useScale,
    useSetStageScrollPosition,
    useStageListLength,
    useViewHeight,
    useViewPortPadding,
    useViewWidth,
} from './stage';

const computeSingleStageAutoScale = (
    viewWidth: number,
    viewHeight: number,
    stageWidth: number,
    stageHeight: number,
    viewPortPadding: number,
) => {
    viewWidth -= viewPortPadding * 2;
    viewHeight -= viewPortPadding * 2;
    const scaleWidth = Math.max(1, viewWidth / stageWidth);
    const scaleHeight = Math.max(1, viewHeight / stageHeight);
    const widthScale = viewWidth > stageWidth ? scaleWidth : viewWidth / stageWidth;
    const heightScale = viewHeight > stageHeight ? scaleHeight : viewHeight / stageHeight;
    return Math.min(4, floor(widthScale < heightScale ? widthScale : heightScale, 10));
};

const computeVarietyStageAutoScale = (viewWidth: number, stageWidth: number) => {
    // 0.6 为多画布的缩放比例
    return Math.min(4, floor((viewWidth * 0.6) / stageWidth, 10));
};

const SingleStage = React.memo(() => {
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    const activeStageIndex = useActiveStageIndex();
    const viewWidth = useViewWidth();
    const viewHeight = useViewHeight();
    const isInit = useRef(false);
    const scale = useScale();
    const isAutoScale = useIsAutoScale();
    const version = useVersion();

    const viewPortPadding = useViewPortPadding();

    const scrollbarsRef = useRef<any>(null);
    const setStageScrollPosition = useSetStageScrollPosition();

    const scrollFrameHandler = useCallback(() => {
        const left = scrollbarsRef.current?.getScrollLeft() ?? 0;
        const top = scrollbarsRef.current?.getScrollTop() ?? 0;
        setStageScrollPosition(left, top);
    }, []) as any;

    const infoRef = useRef({
        width: 0,
        height: 0,
    });
    const [_, refresh] = useState(0);
    const stageSize = infoRef.current;

    const stageWidth = stageSize.width * scale + viewPortPadding * 2;
    const stageHeight = stageSize.height * scale + viewPortPadding * 2;

    useEffect(() => {
        function computeScale(size: ISize) {
            const stageApi = StageAPI(identity);
            const isAutoScale = stageApi.getIsAutoScale();

            let scale = isAutoScale
                ? computeSingleStageAutoScale(viewWidth, viewHeight, size.width, size.height, viewPortPadding)
                : stageApi.getScale();
            if (isAutoScale) {
                scale = Math.min(scale, 1);
            }
            stageApi.setScale(scale);
            const info = infoRef.current;
            if (info.width !== size.width || info.height !== size.height) {
                infoRef.current = {
                    ...size,
                };
                refresh((prev) => prev + 1);
            }
        }
        computeScale(StageAPI(identity).getSize());
        isInit.current = true;
        const handler = coreApi.events.onStageResize(() => {
            const size = StageAPI(identity).getSize();
            computeScale(size);
        });
        return () => coreApi.events.off(handler);
    }, [activeStageIndex, version, viewWidth, viewHeight, isAutoScale, viewPortPadding]);

    if (!isInit.current) return null;

    return (
        <Scrollbars ref={scrollbarsRef} style={{ width: '100%', height: '100%' }} onScrollFrame={scrollFrameHandler}>
            <div
                id={`${identity}ScrollContainer`}
                className="editor-element__scroll-container"
                style={{
                    width: Math.max(stageWidth, viewWidth),
                    height: Math.max(stageHeight, viewHeight),
                }}
            >
                <DragAndDropLayer
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: `translate3D(-50%, -50%, 0)`,
                        width: stageSize.width * scale,
                        height: stageSize.height * scale,
                    }}
                >
                    <StageNode width={stageSize.width} height={stageSize.height} />
                </DragAndDropLayer>
                <StagePlugins />
            </div>
        </Scrollbars>
    );
});

const VarietyStage = React.memo(() => {
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    const scale = useScale();
    const isAutoScale = useIsAutoScale();
    const viewWidth = useViewWidth();
    const viewHeight = useViewHeight();
    const isInit = useRef(false);
    const version = useVersion();

    const scrollbarsRef = useRef<any>(null);
    const setStageScrollPosition = useSetStageScrollPosition();

    const scrollFrameHandler = useCallback(() => {
        // console.log('scrollFrame');
        const left = scrollbarsRef.current?.getScrollLeft() ?? 0;
        const top = scrollbarsRef.current?.getScrollTop() ?? 0;
        setStageScrollPosition(left, top);
    }, []) as any;

    const scrollStopHandler = useCallback(() => {
        // console.log('scrollStop');
    }, []) as any;

    const viewPortPadding = useViewPortPadding();

    const [stageSize, setStageSize] = useState<ISize>(StageAPI(identity).getSize());
    const stageWidth = stageSize.width * scale + viewPortPadding * 2;
    const stageHeight = stageSize.height * scale + viewPortPadding * 2;

    useEffect(() => {
        function computeScale(size: ISize) {
            const stageApi = StageAPI(identity);
            const isAutoScale = stageApi.getIsAutoScale();
            const scale = isAutoScale ? computeVarietyStageAutoScale(viewWidth, size.width) : stageApi.getScale();
            stageApi.setScale(scale);
            setStageSize(size);
        }
        computeScale(StageAPI(identity).getSize());
        isInit.current = true;
        const handler = coreApi.events.onStageResize(() => {
            const size = StageAPI(identity).getSize();
            computeScale(size);
        });
        return () => coreApi.events.off(handler);
    }, [version, viewWidth, viewHeight, isAutoScale, viewPortPadding]);

    if (!isInit.current) return null;

    return (
        <Scrollbars
            ref={scrollbarsRef}
            style={{ width: '100%', height: '100%' }}
            onScrollFrame={scrollFrameHandler}
            onScrollStop={scrollStopHandler}
            renderView={(props) => (
                <div {...props} id={`${identity}ScrollContainerWrap`} className="stage-scroll-container" />
            )}
        >
            <div
                id={`${identity}ScrollContainer`}
                className="editor-element__scroll-container"
                style={{
                    width: Math.floor(Math.max(stageWidth, viewWidth)),
                    height: Math.floor(Math.max(stageHeight, viewHeight)),
                }}
            >
                <DragAndDropLayer
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: `translate3D(-50%, -50%, 0)`,
                        width: stageSize.width * scale,
                        height: stageSize.height * scale,
                    }}
                >
                    <StageNode width={stageSize.width} height={stageSize.height} />
                </DragAndDropLayer>
                <StagePlugins />
            </div>
        </Scrollbars>
    );
});

const StageContainer = React.memo(() => {
    const isLongImageMode = useIsLongImageMode();
    return isLongImageMode ? <VarietyStage /> : <SingleStage />;
});

const useGetPlugins = (plugins: IExportPlugin[]) => {
    return plugins
        .sort((a, b) => a.sort - b.sort)
        .map((plugin) => <React.Fragment key={plugin.name}>{plugin.render?.()}</React.Fragment>);
};

const useListenerPluginsData = (type: PluginType) => {
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    return coreApi.getPlugins()[type];
};

const SystemPlugins = React.memo(() => {
    return (
        <>
            <Listener />
            {useGetPlugins(useListenerPluginsData(PluginType.SYSTEM))}
        </>
    );
});

const ViewportPlugins = React.memo(() => {
    return <>{useGetPlugins(useListenerPluginsData(PluginType.VIEWPORT))}</>;
});

const StagePlugins = React.memo(() => {
    return <>{useGetPlugins(useListenerPluginsData(PluginType.STAGE))}</>;
});

export const App = () => {
    const identity = useIdentity();
    const coreApi = CoreAPI(identity);
    const nodeRef = useRef<HTMLDivElement>(null);

    const viewWidth = useViewWidth();
    const viewHeight = useViewHeight();

    const { run: mouseMoveHandle } = useThrottleFn(
        (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            coreApi.events.emitMouseMove(ev);
        },
        { wait: 5 },
    );

    useEffect(() => {
        const mouseWheelHandle = (ev) => {
            coreApi.events.emitWheel(ev);
        };
        nodeRef.current?.addEventListener('wheel', mouseWheelHandle);
        return () => {
            nodeRef.current?.removeEventListener('wheel', mouseWheelHandle);
        };
    }, []);

    const length = useStageListLength();
    const isStageVisible = useIsStageVisible();

    return (
        <div
            ref={nodeRef}
            data-editor-id={identity}
            className="editor-element"
            style={{ width: viewWidth, height: viewHeight }}
            onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                coreApi.events.emitContextMenu(e);
            }}
            onMouseMove={mouseMoveHandle}
            onMouseDown={(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                coreApi.events.emitMouseDown(ev);
            }}
            onMouseUp={(ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                coreApi.events.emitMouseUp(ev);
            }}
        >
            {viewWidth && viewHeight ? (
                <>
                    <SystemPlugins />
                    {!!length && isStageVisible && <StageContainer />}
                    <ViewportPlugins />
                </>
            ) : null}
        </div>
    );
};
